import React, { useMemo, useCallback } from 'react';
import { Creatable } from '@iq/react-components';
import '../widgets/SelectWidget.scss';

const CreatableField = (properties) => {
  const {
    disabled,
    readonly,
    required,
    formData: value,
    schema: {
      handleCreatable,
      validateCreatable,
      default: defaultValue,
      isMulti,
      isClearable,
      noOptionsMessage,
      placeholder: schemaPlaceholder,
      items: { oneOf } = { oneOf: [] },
      label,
    },
    onChange,
    ...otherProperties
  } = properties;
  const getLabel = (value_) => oneOf.find(({ enum: [v] }) => v === value_)?.title || value_;

  const options = useMemo(
    () =>
      oneOf.map(({ enum: [value_], title }) => ({
        value: value_,
        label: title,
      })),
    [oneOf]
  );

  const existingOptionsSet = useMemo(
    () => new Set(options.map((option) => option.value.toLowerCase())),
    [options]
  );

  const theValue = useMemo(() => {
    if (isMulti) {
      return (value || []).map((v) => ({
        value: v,
        label: oneOf.find(({ enum: [value_] }) => value_ === v).title,
      }));
    }
    if (value || value === 0) {
      return { label: getLabel(value), value };
    }
    if (defaultValue) {
      return { label: getLabel(defaultValue), defaultValue };
    }
  }, [value, defaultValue, oneOf]);

  const setValues = useCallback(
    (selected) => {
      if (isMulti) {
        onChange((selected || []).map((s) => s.value));
      } else {
        onChange(selected && (selected.value || selected.value === 0) ? selected.value : null);
      }
    },
    [onChange, isMulti]
  );

  const getEmptyMessage = (text) => <div>{text}</div>;

  const overrideStyles = {
    container: (base) => ({ ...base, lineHeight: 'normal' }),
    singleValue: (base) => ({ ...base, lineHeight: 'normal' }),
    control: (base) => ({ ...base, lineHeight: 'normal' }),
  };

  const customIsValidNewOption = useCallback(
    (inputValue) => {
      if (!validateCreatable?.(inputValue)) {
        return false;
      }
      return !existingOptionsSet.has(inputValue.toLowerCase());
    },
    [validateCreatable, existingOptionsSet]
  );

  const createValue = useCallback(
    (inputValue) => {
      const trimmedValue = inputValue.trim();

      if (!trimmedValue || (validateCreatable && !validateCreatable(trimmedValue))) {
        return;
      }

      if (isMulti) {
        const selectedValues = value || [];
        const hasSelectedValue = selectedValues.some(
          (selectedValue) => selectedValue.toLowerCase() === trimmedValue.toLowerCase()
        );

        if (hasSelectedValue) {
          return;
        }

        onChange([...selectedValues, trimmedValue]);
        return;
      }

      onChange(trimmedValue);
    },
    [isMulti, onChange, validateCreatable, value]
  );

  return (
    <>
      <label className="control-label">{label}</label>
      <Creatable
        {...otherProperties}
        className="schema-select-widget"
        isMulti={isMulti}
        isClearable={isClearable}
        closeMenuOnSelect={!isMulti}
        disabled={disabled}
        readOnly={readonly}
        required={required}
        placeholder={schemaPlaceholder}
        menuPlacement="auto"
        value={theValue}
        onChange={setValues}
        isValidNewOption={customIsValidNewOption}
        onCreateOption={handleCreatable || createValue}
        options={options}
        noOptionsMessage={() => getEmptyMessage(noOptionsMessage || 'No options')}
        styles={overrideStyles}
      />
    </>
  );
};

export default CreatableField;
